import type { PublicKey } from '@shared/types';
import { base64ToBytes, bytesToBase64, toArrayBuffer, utf8ToBytes, bytesToUtf8 } from '../utils/encoding';
import { decryptMessageAsyncWithKey, encryptMessageAsyncWithKey } from '../crypto/messageEncryption';

const DIRECT_KEY_ENVELOPE_PREFIX = 'dk2:';
const DIRECT_CIPHERTEXT_PREFIX = 'dcm1:';
const DIRECT_KEY_BYTES = 32;
const DIRECT_NONCE_BYTES = 12;

export function isDirectEpochCiphertext(ciphertext: string | null | undefined): boolean {
  return typeof ciphertext === 'string' && ciphertext.startsWith(DIRECT_CIPHERTEXT_PREFIX);
}

export async function computeDirectKeyCommitment(rawKey: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', toArrayBuffer(rawKey));
  const bytes = new Uint8Array(digest);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function parseDirectKeyEnvelope(envelopeCiphertext: string): { publisherIdentityId: string; ciphertext: string } | null {
  if (!envelopeCiphertext.startsWith(DIRECT_KEY_ENVELOPE_PREFIX)) return null;
  const rest = envelopeCiphertext.slice(DIRECT_KEY_ENVELOPE_PREFIX.length);
  const idx = rest.indexOf(':');
  if (idx <= 0) return null;
  const publisherIdentityId = rest.slice(0, idx).trim();
  const ciphertext = rest.slice(idx + 1);
  if (!publisherIdentityId || !ciphertext) return null;
  return { publisherIdentityId, ciphertext };
}

export async function encryptDirectKeyEnvelope(params: {
  rawKey: Uint8Array;
  publisherIdentityId: string;
  publisherEcdhKey: CryptoKey | null;
  publisherEcdhScalar: Uint8Array;
  recipientPublicKey: PublicKey;
}): Promise<string> {
  if (params.rawKey.length !== DIRECT_KEY_BYTES) {
    throw new Error(`Direct chat key must be ${DIRECT_KEY_BYTES} bytes`);
  }
  const plaintext = bytesToBase64(params.rawKey);
  const ciphertext = await encryptMessageAsyncWithKey(
    plaintext,
    params.recipientPublicKey,
    params.publisherEcdhKey,
    params.publisherEcdhScalar
  );
  return `${DIRECT_KEY_ENVELOPE_PREFIX}${params.publisherIdentityId}:${ciphertext}`;
}

export async function decryptDirectKeyEnvelope(params: {
  envelopeCiphertext: string;
  publisherPublicKey: PublicKey;
  recipientEcdhKey: CryptoKey | null;
  recipientEcdhScalar: Uint8Array;
}): Promise<Uint8Array | null> {
  const parsed = parseDirectKeyEnvelope(params.envelopeCiphertext);
  if (!parsed) return null;
  const plaintext = await decryptMessageAsyncWithKey(
    parsed.ciphertext,
    params.publisherPublicKey,
    params.recipientEcdhKey,
    params.recipientEcdhScalar
  );
  if (!plaintext) return null;
  const raw = base64ToBytes(plaintext);
  if (raw.length !== DIRECT_KEY_BYTES) return null;
  return raw;
}

export function generateDirectKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(DIRECT_KEY_BYTES));
}

async function importDirectKey(rawKey: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', toArrayBuffer(rawKey), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

export async function encryptDirectEpochMessage(plaintext: string, rawKey: Uint8Array): Promise<string> {
  const key = await importDirectKey(rawKey);
  const nonce = crypto.getRandomValues(new Uint8Array(DIRECT_NONCE_BYTES));
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: toArrayBuffer(nonce) }, key, toArrayBuffer(utf8ToBytes(plaintext)));
  const payload = new Uint8Array(nonce.length + encrypted.byteLength);
  payload.set(nonce, 0);
  payload.set(new Uint8Array(encrypted), nonce.length);
  return `${DIRECT_CIPHERTEXT_PREFIX}${bytesToBase64(payload)}`;
}

export async function decryptDirectEpochMessage(ciphertext: string, rawKey: Uint8Array): Promise<string | null> {
  if (!isDirectEpochCiphertext(ciphertext)) return null;
  const payload = base64ToBytes(ciphertext.slice(DIRECT_CIPHERTEXT_PREFIX.length));
  if (payload.length <= DIRECT_NONCE_BYTES) return null;
  const nonce = payload.slice(0, DIRECT_NONCE_BYTES);
  const encrypted = payload.slice(DIRECT_NONCE_BYTES);
  try {
    const key = await importDirectKey(rawKey);
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: toArrayBuffer(nonce) }, key, toArrayBuffer(encrypted));
    return bytesToUtf8(new Uint8Array(decrypted));
  } catch {
    return null;
  }
}
